/**
 * Avatar Component Class
 * --------------------
 * Creates and manages avatar elements with team-based colors and initials.
 * Supports both image-based avatars and initial-based avatars with color coding
 * based on team ID.
 */
class Avatar {
  constructor(options = {}) {
    // Default configuration with team-specific colors and initials
    this.config = {
      size: options.size || 48,
      fontWeight: options.fontWeight || 500,
      fontSize: options.fontSize || 16,
      teams: {
        // RLP (no team)
        0: { color: '#f56565', initial: 'R' },
        // Prospect team
        6: { color: '#2196F3', initial: 'P' },
        // Commercial team
        7: { color: '#4CAF50', initial: 'C' },
        // Default
        default: { color: '#9e9e9e', initial: '-' }
      }
    };

    // Create and inject styles
    this._injectStyles();
  }

  /**
   * Get team-specific color and initial based on object properties
   * @param {Object} obj - Object containing type, teamId and other identifiers
   * @returns {Object} Team information containing color and initial
   */
  getTeamInfo(obj) {
    // If teamId is present, use it for color and initial
    if (obj.teamId) {
      const numericTeamId = typeof obj.teamId === 'string' ? parseInt(obj.teamId, 10) : obj.teamId;
      return this.config.teams[numericTeamId] || this.config.teams.default;
    }

    // For Account type with no teamId, use red color
    if (obj.type === 'Account') {
      return this.config.teams[0]; // Red color and 'R' initial
    }

    // For Contact type with no teamId but non-zero personId, use red color
    if (obj.type === 'Contact' && obj.personId && parseInt(obj.personId, 10) !== 0) {
      return this.config.teams[0]; // Red color and 'R' initial
    }

    if (obj.rlpPersonId && parseInt(obj.rlpPersonId, 10) !== 0) {
      return this.config.teams[0]; // Red color and 'R' initial
    }

    // Default case
    return this.config.teams.default;
  }

  /**
   * Create an avatar element
   * @param {Object} obj - Object information containing teamId and optional avatarUrl
   * @returns {HTMLElement} Avatar element
   */
  create(obj) {
    const avatarElement = document.createElement('div');
    avatarElement.className = 'avatar';

    this._applyBaseStyles(avatarElement);

    if (obj.avatarUrl) {
      return this._createImageAvatar(avatarElement, obj.avatarUrl);
    } else {
      const teamInfo = this.getTeamInfo(obj);
      return this._createInitialsAvatar(avatarElement, teamInfo);
    }
  }

  /**
   * Apply base avatar styles to element
   * @private
   * @param {HTMLElement} element - Element to style
   */
  _applyBaseStyles(element) {
    element.style.width = `${this.config.size}px`;
    element.style.height = `${this.config.size}px`;
    element.style.borderRadius = '50%';
    element.style.display = 'grid';
    element.style.placeItems = 'center';
    element.style.flexShrink = '0';
    element.style.userSelect = 'none';
  }

  /**
   * Create image-based avatar
   * @private
   * @param {HTMLElement} element - Base avatar element
   * @param {string} url - Image URL
   * @returns {HTMLElement} Avatar element with image
   */
  _createImageAvatar(element, url) {
    const img = document.createElement('img');
    img.className = 'avatar-image';
    img.src = url;
    img.alt = '';
    img.style.width = '100%';
    img.style.height = '100%';
    img.style.borderRadius = '50%';
    img.style.objectFit = 'cover';

    element.appendChild(img);
    return element;
  }

  /**
   * Create initials-based avatar
   * @private
   * @param {HTMLElement} element - Base avatar element
   * @param {Object} teamInfo - Team color and initial information
   * @returns {HTMLElement} Avatar element with initial and background color
   */
  _createInitialsAvatar(element, teamInfo) {
    element.style.backgroundColor = teamInfo.color;
    element.style.color = '#fff';
    element.style.fontWeight = this.config.fontWeight;
    element.style.fontSize = `${this.config.fontSize}px`;
    element.textContent = teamInfo.initial;
    return element;
  }

  /**
   * Inject required CSS styles into document
   * @private
   */
  _injectStyles() {
    if (!document.querySelector('#avatar-styles')) {
      const style = document.createElement('style');
      style.id = 'avatar-styles';
      style.textContent = `
        .avatar {
          position: relative;
          overflow: hidden;
        }
        .avatar-image {
          position: absolute;
          top: 0;
          left: 0;
        }
      `;
      document.head.appendChild(style);
    }
  }
}

// Export for use in other files
export { Avatar };
